// src/components/attestations/AttestationDocumentsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    fetchAttestationDocuments,
    deleteAttestationDocument,
    fetchAttestation,
    clearDocumentError,
    clearDocumentSuccess,
} from '../../store/actions/attestationActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import DocumentViewer from './DocumentViewer';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import DocumentUploadModal from './DocumentUploadModal';

const AttestationDocumentsPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const {
        documents,
        documentsMeta,
        selectedAttestation,
        loading,
        error,
        success,
        uploadProgress
    } = useSelector((state) => state.attestations);

    const [selectedDocument, setSelectedDocument] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showViewer, setShowViewer] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [documentToDelete, setDocumentToDelete] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all'); // all, images, pdfs, docs
    const [page, setPage] = useState(1);
    const [limit] = useState(12);

    // Styles object
    const styles = {
        container: {
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '2rem 1rem',
        },
        header: {
            marginBottom: '2rem',
        },
        headerTop: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
        },
        backButton: {
            padding: '0.5rem',
            backgroundColor: '#f3f4f6',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
        },
        backButtonHover: {
            backgroundColor: '#e5e7eb',
        },
        title: {
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#111827',
            margin: 0,
        },
        attestationInfo: {
            marginTop: '0.5rem',
            padding: '1rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        attestationDetails: {
            display: 'flex',
            gap: '2rem',
        },
        attestationNumber: {
            fontWeight: 600,
            color: '#2563eb',
        },
        clientName: {
            color: '#374151',
        },
        viewAttestationButton: {
            padding: '0.5rem 1rem',
            backgroundColor: '#ffffff',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            textDecoration: 'none',
            transition: 'all 0.2s',
        },
        uploadArea: {
            marginBottom: '2rem',
            padding: '2rem',
            border: `2px dashed ${dragActive ? '#2563eb' : '#d1d5db'}`,
            borderRadius: '0.5rem',
            backgroundColor: dragActive ? '#eff6ff' : '#f9fafb',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        uploadIcon: {
            width: '3rem',
            height: '3rem',
            marginBottom: '1rem',
            color: dragActive ? '#2563eb' : '#9ca3af',
        },
        uploadTitle: {
            fontSize: '1.125rem',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '0.5rem',
        },
        uploadSubtitle: {
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '1rem',
        },
        uploadButton: {
            padding: '0.75rem 1.5rem',
            backgroundColor: '#ffffff',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
        },
        uploadButtonHover: {
            backgroundColor: '#f9fafb',
        },
        uploadProgress: {
            marginTop: '1rem',
            height: '0.5rem',
            backgroundColor: '#e5e7eb',
            borderRadius: '9999px',
            overflow: 'hidden',
        },
        progressBar: {
            height: '100%',
            backgroundColor: '#2563eb',
            transition: 'width 0.3s ease',
        },
        controls: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
        },
        leftControls: {
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
        },
        filterButtons: {
            display: 'flex',
            gap: '0.5rem',
        },
        filterButton: {
            padding: '0.5rem 1rem',
            background: 'none',
            border: '1px solid #e5e7eb',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            cursor: 'pointer',
            color: '#374151',
            transition: 'all 0.2s',
        },
        filterButtonActive: {
            backgroundColor: '#2563eb',
            color: '#ffffff',
            borderColor: '#2563eb',
        },
        viewToggle: {
            display: 'flex',
            gap: '0.25rem',
            padding: '0.25rem',
            backgroundColor: '#f3f4f6',
            borderRadius: '0.375rem',
        },
        viewToggleButton: {
            padding: '0.375rem 0.75rem',
            background: 'none',
            border: 'none',
            borderRadius: '0.25rem',
            fontSize: '0.875rem',
            cursor: 'pointer',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            transition: 'all 0.2s',
        },
        viewToggleButtonActive: {
            backgroundColor: '#ffffff',
            color: '#111827',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        },
        statsBar: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem',
            padding: '1.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
        },
        statCard: {
            textAlign: 'center',
        },
        statValue: {
            fontSize: '1.5rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.25rem',
        },
        statLabel: {
            fontSize: '0.875rem',
            color: '#6b7280',
        },
        documentGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem',
        },
        documentCard: {
            padding: '1rem',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            position: 'relative',
        },
        documentCardHover: {
            transform: 'translateY(-4px)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            borderColor: '#2563eb',
        },
        documentIcon: {
            fontSize: '3rem',
            marginBottom: '0.75rem',
            textAlign: 'center',
        },
        documentName: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#111827',
            marginBottom: '0.25rem',
            wordBreak: 'break-word',
            lineHeight: '1.4',
        },
        documentMeta: {
            fontSize: '0.75rem',
            color: '#6b7280',
            marginBottom: '0.5rem',
        },
        documentType: {
            display: 'inline-block',
            padding: '0.125rem 0.375rem',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            borderRadius: '0.25rem',
            fontSize: '0.625rem',
            fontWeight: 500,
            textTransform: 'uppercase',
        },
        documentActions: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '0.75rem',
        },
        actionButtons: {
            display: 'flex',
            gap: '0.25rem',
        },
        iconButton: {
            padding: '0.25rem',
            background: 'none',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            color: '#6b7280',
            transition: 'all 0.2s',
        },
        iconButtonHover: {
            backgroundColor: '#f3f4f6',
        },
        deleteButton: {
            color: '#dc2626',
        },
        deleteButtonHover: {
            backgroundColor: '#fee2e2',
        },
        listView: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            marginBottom: '2rem',
        },
        listItem: {
            display: 'flex',
            alignItems: 'center',
            padding: '1rem',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        listItemHover: {
            backgroundColor: '#f9fafb',
            borderColor: '#d1d5db',
        },
        listIcon: {
            fontSize: '2rem',
            marginRight: '1rem',
        },
        listInfo: {
            flex: 1,
        },
        listName: {
            fontSize: '1rem',
            fontWeight: 500,
            color: '#111827',
            marginBottom: '0.25rem',
        },
        listMeta: {
            display: 'flex',
            gap: '1rem',
            fontSize: '0.875rem',
            color: '#6b7280',
        },
        emptyState: {
            textAlign: 'center',
            padding: '4rem 2rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '2px dashed #e5e7eb',
        },
        emptyIcon: {
            width: '4rem',
            height: '4rem',
            marginBottom: '1rem',
            color: '#9ca3af',
        },
        emptyTitle: {
            fontSize: '1.125rem',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '0.5rem',
        },
        emptyText: {
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '1.5rem',
        },
        footer: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: '2rem',
        },
    };

    useEffect(() => {
        if (id) {
            dispatch(fetchAttestation(id));
            fetchDocuments();
        }
    }, [dispatch,  id]);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = () => {
        const params = {
            page,
            limit,
            type: filter !== 'all' ? filter : undefined,
            search: searchTerm || undefined,
        };
        dispatch(fetchAttestationDocuments(id, params));
    };

    useEffect(() => {
        return () => {
            dispatch(clearDocumentError());
            dispatch(clearDocumentSuccess());
        };
    }, [dispatch]);

    // const handleDrag = useCallback((e) => {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     if (e.type === 'dragenter' || e.type === 'dragover') {
    //         setDragActive(true);
    //     } else if (e.type === 'dragleave') {
    //         setDragActive(false);
    //     }
    // }, []);

    // const handleDrop = useCallback((e) => {
    //     e.preventDefault();
    //     e.stopPropagation();
    //     setDragActive(false);

    //     const files = Array.from(e.dataTransfer.files);
    //     handleFiles(files);
    // }, []);

    // const handleFileSelect = useCallback((e) => {
    //     const files = Array.from(e.target.files);
    //     handleFiles(files);
    // }, []);

    // const handleFiles = async (files) => {
    //     setUploading(true);
    //     for (const file of files) {
    //         try {
    //             await dispatch(uploadAttestationDocument(id, file));
    //         } catch (error) {
    //             console.error('Upload failed:', error);
    //         }
    //     }
    //     setUploading(false);
    //     fetchDocuments();
    // };

    const handleDeleteClick = (document, e) => {
        e.stopPropagation();
        setDocumentToDelete(document);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await dispatch(deleteAttestationDocument(id, documentToDelete.id));
            setShowDeleteConfirm(false);
            setDocumentToDelete(null);
            fetchDocuments();
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    const handleUploadComplete = () => {
        fetchDocuments(); // Refresh the document list
    };

    const handleDocumentClick = (document) => {
        setSelectedDocument(document);
        setShowViewer(true);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        setPage(1);
    };

    const getFileIcon = (fileType, fileName) => {
        const extension = fileName?.split('.').pop()?.toLowerCase();

        if (fileType?.startsWith('image/')) return '🖼️';
        if (fileType?.includes('pdf') || extension === 'pdf') return '📄';
        if (fileType?.includes('word') || ['doc', 'docx'].includes(extension)) return '📝';
        if (fileType?.includes('excel') || ['xls', 'xlsx'].includes(extension)) return '📊';
        if (fileType?.includes('powerpoint') || ['ppt', 'pptx'].includes(extension)) return '📽️';
        if (fileType?.includes('text') || extension === 'txt') return '📃';
        if (['zip', 'rar', '7z'].includes(extension)) return '🗜️';
        if (['mp3', 'wav', 'ogg'].includes(extension)) return '🎵';
        if (['mp4', 'avi', 'mov'].includes(extension)) return '🎬';
        return '📎';
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getStats = () => {
        if (!documents) return { total: 0, totalSize: 0, images: 0, pdfs: 0, docs: 0 };

        return documents.reduce((acc, doc) => {
            acc.total++;
            acc.totalSize += doc.fileSize || 0;

            const ext = doc.name?.split('.').pop()?.toLowerCase();
            if (doc.fileType?.startsWith('image/')) acc.images++;
            else if (doc.fileType?.includes('pdf') || ext === 'pdf') acc.pdfs++;
            else if (['doc', 'docx'].includes(ext)) acc.docs++;

            return acc;
        }, { total: 0, totalSize: 0, images: 0, pdfs: 0, docs: 0 });
    };

    const stats = getStats();
    const filteredDocuments = documents?.filter(doc => {
        if (filter === 'all') return true;
        if (filter === 'images') return doc.fileType?.startsWith('image/');
        if (filter === 'pdfs') return doc.fileType?.includes('pdf') || doc.name?.endsWith('.pdf');
        if (filter === 'docs') {
            const ext = doc.name?.split('.').pop()?.toLowerCase();
            return ['doc', 'docx'].includes(ext);
        }
        return true;
    });

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerTop}>
                    <div style={styles.headerLeft}>
                        <button
                            onClick={() => navigate(`/admin/attestations/${id}/details`)}
                            style={styles.backButton}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
                            }}
                        >
                            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <h1 style={styles.title}>Gestion des documents</h1>
                    </div>
                    <button
                        onClick={() => setShowUploadModal(true)}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#059669';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#10b981';
                        }}
                    >
                        <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Uploader un document
                    </button>
                </div>

                {selectedAttestation && (
                    <div style={styles.attestationInfo}>
                        <div style={styles.attestationDetails}>
                            <div>
                                <span style={styles.attestationNumber}>
                                    {selectedAttestation.numAttestation || 'Nouvelle attestation'}
                                </span>
                            </div>
                            <div style={styles.clientName}>
                                {selectedAttestation.firstName} {selectedAttestation.lastName}
                            </div>
                        </div>
                        <Link
                            to={`/admin/attestations/${id}/details`}
                            style={styles.viewAttestationButton}
                        >
                            Voir l'attestation
                        </Link>
                    </div>
                )}
            </div>

            {error && (
                <Alert
                    type="error"
                    message={error}
                    onClose={() => dispatch(clearDocumentError())}
                />
            )}

            {success && (
                <Alert
                    type="success"
                    message={success}
                    onClose={() => dispatch(clearDocumentSuccess())}
                />
            )}

            {/* Upload Area */}
            {/* <div
                style={styles.uploadArea}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-upload').click()}
            >
                <svg
                    style={styles.uploadIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                </svg>
                <div style={styles.uploadTitle}>
                    Glissez-déposez vos fichiers ici
                </div>
                <div style={styles.uploadSubtitle}>
                    ou cliquez pour sélectionner des fichiers
                </div>
                <input
                    type="file"
                    id="file-upload"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />
                <button
                    type="button"
                    style={styles.uploadButton}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = styles.uploadButtonHover.backgroundColor;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = styles.uploadButton.backgroundColor;
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById('file-upload').click();
                    }}
                >
                    <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Sélectionner des fichiers
                </button>
                {uploading && uploadProgress > 0 && (
                    <div style={styles.uploadProgress}>
                        <div style={{ ...styles.progressBar, width: `${uploadProgress}%` }} />
                    </div>
                )}
            </div> */}

            {/* Stats */}
            <div style={styles.statsBar}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.total}</div>
                    <div style={styles.statLabel}>Total documents</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{formatFileSize(stats.totalSize)}</div>
                    <div style={styles.statLabel}>Taille totale</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.images}</div>
                    <div style={styles.statLabel}>Images</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.pdfs}</div>
                    <div style={styles.statLabel}>PDFs</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{stats.docs}</div>
                    <div style={styles.statLabel}>Documents</div>
                </div>
            </div>

            {/* Controls */}
            <div style={styles.controls}>
                <div style={styles.leftControls}>
                    <div style={styles.filterButtons}>
                        <button
                            style={{
                                ...styles.filterButton,
                                ...(filter === 'all' ? styles.filterButtonActive : {}),
                            }}
                            onClick={() => setFilter('all')}
                        >
                            Tous
                        </button>
                        <button
                            style={{
                                ...styles.filterButton,
                                ...(filter === 'images' ? styles.filterButtonActive : {}),
                            }}
                            onClick={() => setFilter('images')}
                        >
                            Images
                        </button>
                        <button
                            style={{
                                ...styles.filterButton,
                                ...(filter === 'pdfs' ? styles.filterButtonActive : {}),
                            }}
                            onClick={() => setFilter('pdfs')}
                        >
                            PDFs
                        </button>
                        <button
                            style={{
                                ...styles.filterButton,
                                ...(filter === 'docs' ? styles.filterButtonActive : {}),
                            }}
                            onClick={() => setFilter('docs')}
                        >
                            Documents
                        </button>
                    </div>
                </div>
                <div style={styles.viewToggle}>
                    <button
                        style={{
                            ...styles.viewToggleButton,
                            ...(viewMode === 'grid' ? styles.viewToggleButtonActive : {}),
                        }}
                        onClick={() => setViewMode('grid')}
                    >
                        <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        Grille
                    </button>
                    <button
                        style={{
                            ...styles.viewToggleButton,
                            ...(viewMode === 'list' ? styles.viewToggleButtonActive : {}),
                        }}
                        onClick={() => setViewMode('list')}
                    >
                        <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        Liste
                    </button>
                </div>
            </div>

            {/* Search */}
            <div style={{ marginBottom: '1.5rem' }}>
                <SearchBar
                    onSearch={handleSearch}
                    placeholder="Rechercher un document par nom..."
                    fullWidth
                />
            </div>

            {/* Documents Display */}
            {loading && !documents?.length ? (
                <LoadingSpinner />
            ) : (
                <>
                    {filteredDocuments?.length > 0 ? (
                        <>
                            {viewMode === 'grid' ? (
                                <div style={styles.documentGrid}>
                                    {filteredDocuments.map((doc) => (
                                        <div
                                            key={doc.id}
                                            style={styles.documentCard}
                                            onClick={() => handleDocumentClick(doc)}
                                            onMouseEnter={(e) => {
                                                Object.assign(e.currentTarget.style, styles.documentCardHover);
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'none';
                                                e.currentTarget.style.boxShadow = 'none';
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                            }}
                                        >
                                            <div style={styles.documentIcon}>
                                                {getFileIcon(doc.fileType, doc.name)}
                                            </div>
                                            <div style={styles.documentName}>
                                                {doc.originalName || doc.name}
                                            </div>
                                            <div style={styles.documentMeta}>
                                                {formatFileSize(doc.fileSize)}
                                            </div>
                                            <div style={styles.documentType}>
                                                {doc.fileType?.split('/')[1] || doc.name?.split('.').pop()}
                                            </div>
                                            <div style={styles.documentActions}>
                                                <span style={styles.documentMeta}>
                                                    {new Date(doc.createdAt).toLocaleDateString()}
                                                </span>
                                                <div style={styles.actionButtons}>
                                                    <button
                                                        style={styles.iconButton}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = styles.iconButtonHover.backgroundColor;
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(doc.storagePath, '_blank');
                                                        }}
                                                    >
                                                        <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        style={{
                                                            ...styles.iconButton,
                                                            ...styles.deleteButton,
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = 'transparent';
                                                        }}
                                                        onClick={(e) => handleDeleteClick(doc, e)}
                                                    >
                                                        <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={styles.listView}>
                                    {filteredDocuments.map((doc) => (
                                        <div
                                            key={doc.id}
                                            style={styles.listItem}
                                            onClick={() => handleDocumentClick(doc)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = styles.listItemHover.backgroundColor;
                                                e.currentTarget.style.borderColor = styles.listItemHover.borderColor;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = '#ffffff';
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                            }}
                                        >
                                            <span style={styles.listIcon}>
                                                {getFileIcon(doc.fileType, doc.name)}
                                            </span>
                                            <div style={styles.listInfo}>
                                                <div style={styles.listName}>
                                                    {doc.originalName || doc.name}
                                                </div>
                                                <div style={styles.listMeta}>
                                                    <span>{formatFileSize(doc.fileSize)}</span>
                                                    <span>{new Date(doc.createdAt).toLocaleDateString()}</span>
                                                    <span>{doc.fileType?.split('/')[1] || doc.name?.split('.').pop()}</span>
                                                </div>
                                            </div>
                                            <div style={styles.actionButtons}>
                                                <button
                                                    style={styles.iconButton}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = styles.iconButtonHover.backgroundColor;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(doc.storagePath, '_blank');
                                                    }}
                                                >
                                                    <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                </button>
                                                <button
                                                    style={{
                                                        ...styles.iconButton,
                                                        ...styles.deleteButton,
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                    onClick={(e) => handleDeleteClick(doc, e)}
                                                >
                                                    <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Pagination */}
                            {documentsMeta && documentsMeta.totalPages > 1 && (
                                <div style={styles.footer}>
                                    <Pagination
                                        currentPage={page}
                                        totalPages={documentsMeta.totalPages}
                                        onPageChange={setPage}
                                    />
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={styles.emptyState}>
                            <svg style={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                            </svg>
                            <div style={styles.emptyTitle}>
                                Aucun document trouvé
                            </div>
                            <div style={styles.emptyText}>
                                {searchTerm || filter !== 'all'
                                    ? 'Aucun document ne correspond à vos critères de recherche'
                                    : 'Commencez par télécharger des documents en glissant-déposant ou en cliquant sur la zone de téléchargement'}
                            </div>
                            {(searchTerm || filter !== 'all') && (
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setFilter('all');
                                    }}
                                    style={styles.uploadButton}
                                >
                                    Réinitialiser les filtres
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* Document Viewer Modal */}
            {showViewer && (
                <DocumentViewer
                    document={selectedDocument}
                    attestationId={id} // Make sure this is passed
                    onClose={() => {
                        setShowViewer(false);
                        setSelectedDocument(null);
                    }}
                />
            )}

            {showUploadModal && (
                <DocumentUploadModal
                    attestationId={id}
                    onClose={() => setShowUploadModal(false)}
                    onUploadComplete={handleUploadComplete}
                />
            )}

            {/* Delete Confirmation */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDocumentToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                title="Supprimer le document"
                message={`Êtes-vous sûr de vouloir supprimer le document "${documentToDelete?.originalName || documentToDelete?.name}" ?`}
                confirmText="Supprimer"
                cancelText="Annuler"
                type="danger"
            />
        </div>
    );
};

export default AttestationDocumentsPage;